
import React, { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Download, ArrowUpDown } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

// Mock data for payment transactions
const mockPayments = [
  {
    id: "1",
    userId: "243",
    userName: "João Silva",
    amount: 49.90,
    plan: "premium",
    status: "completed",
    date: new Date(2024, 4, 2)
  },
  {
    id: "2",
    userId: "187",
    userName: "Maria Oliveira",
    amount: 99.90,
    plan: "vip",
    status: "completed",
    date: new Date(2024, 4, 1)
  },
  {
    id: "3",
    userId: "321",
    userName: "Carlos Pereira",
    amount: 49.90,
    plan: "premium",
    status: "processing",
    date: new Date(2024, 4, 3)
  },
  {
    id: "4",
    userId: "112",
    userName: "Ana Santos",
    amount: 99.90,
    plan: "vip",
    status: "failed",
    date: new Date(2024, 4, 2)
  },
  {
    id: "5",
    userId: "276",
    userName: "Roberto Lima",
    amount: 49.90,
    plan: "premium",
    status: "completed",
    date: new Date(2024, 4, 1)
  },
];

const AdminPayments = () => {
  const [payments, setPayments] = useState(mockPayments);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const { toast } = useToast();
  
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', { 
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  const handleExportReport = () => {
    toast({
      title: "Relatório exportado",
      description: "O relatório foi gerado e está pronto para download.",
    });
    // Implementar exportação real (CSV, PDF, etc.)
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = 
      payment.userName.toLowerCase().includes(searchTerm.toLowerCase()) || 
      payment.userId.includes(searchTerm);
    
    const matchesStatus = statusFilter === "all" || payment.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex gap-2 flex-1">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar pagamentos..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="completed">Concluídos</SelectItem>
              <SelectItem value="processing">Em processamento</SelectItem>
              <SelectItem value="failed">Falhou</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button className="flex gap-1" onClick={handleExportReport}>
          <Download size={16} />
          <span>Exportar Relatório</span>
        </Button>
      </div>

      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Usuário</TableHead>
              <TableHead>Plano</TableHead>
              <TableHead>
                <div className="flex items-center">
                  Valor
                  <ArrowUpDown size={14} className="ml-1" />
                </div>
              </TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Data</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredPayments.length > 0 ? (
              filteredPayments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell className="font-medium">{payment.id}</TableCell>
                  <TableCell>
                    <div>
                      <div>{payment.userName}</div>
                      <div className="text-xs text-muted-foreground">ID: {payment.userId}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      payment.plan === "premium" ? "bg-blue-500" :
                      payment.plan === "vip" ? "bg-purple-500" :
                      "bg-green-500"
                    }>
                      {payment.plan.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatCurrency(payment.amount)}</TableCell>
                  <TableCell>
                    <Badge variant={
                      payment.status === "completed" ? "default" : 
                      payment.status === "processing" ? "outline" : 
                      "destructive"
                    }>
                      {payment.status === "completed" ? "Concluído" : 
                       payment.status === "processing" ? "Processando" : 
                       "Falhou"}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(payment.date)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-4 text-muted-foreground">
                  Nenhum pagamento encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminPayments;
